import { h } from 'preact';

/**
 * Alert component for displaying error, warning, success, and info messages
 *
 * @param {Object} props
 * @param {'error'|'warning'|'success'|'info'} props.type - Type of alert
 * @param {string} props.title - Optional title/heading
 * @param {string|preact.VNode} props.children - Alert message content
 * @param {Function} props.onClose - Optional close handler
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.bordered - Show border variant (default: false)
 * @param {boolean} props.leftAccent - Show left accent bar (default: false)
 */
export default function Alert({
  type = 'info',
  title,
  children,
  onClose,
  className = '',
  bordered = false,
  leftAccent = false
}) {
  // Alert variants configuration
  const variants = {
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      iconColor: 'text-red-600',
      accentBorder: 'border-red-500',
      icon: (
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      )
    },
    warning: {
      bg: 'bg-yellow-100',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      iconColor: 'text-yellow-600',
      accentBorder: 'border-yellow-500',
      icon: (
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      )
    },
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      iconColor: 'text-green-600',
      accentBorder: 'border-green-500',
      icon: (
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      )
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      iconColor: 'text-blue-600',
      accentBorder: 'border-blue-500',
      icon: (
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      )
    }
  };

  const variant = variants[type];

  // Build CSS classes
  const borderClasses = bordered
    ? `border ${variant.border}`
    : '';

  const leftAccentClasses = leftAccent
    ? `border-l-4 ${variant.accentBorder}`
    : '';

  const baseClasses = `alert-block p-4 mb-4 ${variant.bg} ${variant.text} ${borderClasses} ${leftAccentClasses}`;
  const roundedClasses = bordered && !leftAccent ? 'rounded-lg' : '';

  return (
    <div
      className={`${baseClasses} ${roundedClasses} ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <svg
          className={`w-5 h-5 ${variant.iconColor} shrink-0 mt-0.5`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          {variant.icon}
        </svg>

        {/* Content */}
        <div className="flex-1">
          {title && (
            <div className="text-sm font-medium mb-1">{title}</div>
          )}
          <div className={`text-sm ${title ? '' : 'mt-0'}`}>
            {children}
          </div>
        </div>

        {/* Close button */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={`${variant.iconColor} hover:opacity-70 transition-opacity shrink-0 cursor-pointer`}
            aria-label="Close alert"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
